/* Command palette (⌘K / Ctrl+K).
 *
 * Specification section 9 asks for a command palette and global search. A
 * trading terminal is used by people who prefer the keyboard, and a palette
 * beats hunting through menus while the market moves.
 */
import { useEffect, useMemo, useState } from 'react'
import { useStore } from '@/store'

interface Command {
  id: string
  label: string
  hint?: string
  group: string
  run: () => void
}

export function CommandPalette() {
  const open = useStore((s) => s.commandPaletteOpen)
  const setOpen = useStore((s) => s.setCommandPalette)
  const toggleTheme = useStore((s) => s.toggleTheme)
  const setBlotter = useStore((s) => s.setActiveBlotter)
  const quotes = useStore((s) => s.quotes)
  const select = useStore((s) => s.selectInstrument)

  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(!open)
      }
      if (e.key === 'Escape' && open) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  const commands = useMemo<Command[]>(() => {
    const base: Command[] = [
      { id: 'theme', label: 'Toggle light / dark theme', group: 'View',
        run: () => toggleTheme() },
      { id: 'positions', label: 'Show positions', group: 'Navigate',
        run: () => setBlotter('positions') },
      { id: 'orders', label: 'Show orders', group: 'Navigate',
        run: () => setBlotter('orders') },
      { id: 'executions', label: 'Show executions', group: 'Navigate',
        run: () => setBlotter('executions') },
    ]
    // Instruments are searchable from the palette too: global search over
    // instruments is what a trader reaches for most often.
    for (const q of quotes.values()) {
      base.push({
        id: `inst-${q.instrument}`,
        label: q.symbol ?? String(q.instrument),
        hint: q.exchange ?? 'instrument',
        group: 'Instruments',
        run: () => select(String(q.instrument)),
      })
    }
    return base
  }, [quotes, toggleTheme, setBlotter, select])

  const filtered = useMemo(() => {
    const f = query.trim().toLowerCase()
    if (!f) return commands.slice(0, 12)
    return commands
      .filter((c) => c.label.toLowerCase().includes(f) || c.group.toLowerCase().includes(f))
      .slice(0, 20)
  }, [commands, query])

  useEffect(() => setIndex(0), [query])

  if (!open) return null

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', justifyContent: 'center', paddingTop: '12vh',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
    >
      <div style={{
        width: 560, maxWidth: '92vw', height: 'fit-content', maxHeight: '64vh',
        background: 'var(--bg-panel)', border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault(); setIndex((i) => Math.min(i + 1, filtered.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault(); setIndex((i) => Math.max(i - 1, 0))
            } else if (e.key === 'Enter') {
              filtered[index]?.run(); setOpen(false); setQuery('')
            }
          }}
          placeholder="Search commands and instruments…"
          style={{
            padding: 'var(--sp-4)', background: 'transparent', border: 'none',
            borderBottom: '1px solid var(--border)',
            color: 'var(--text-primary)', fontSize: 'var(--fs-lg)', outline: 'none',
          }}
        />
        <div style={{ overflowY: 'auto', padding: 'var(--sp-1) 0' }}>
          {filtered.length === 0 && (
            <div style={{
              padding: 'var(--sp-4)', color: 'var(--text-muted)',
              fontSize: 'var(--fs-sm)', textAlign: 'center',
            }}>
              No matches
            </div>
          )}
          {filtered.map((c, i) => (
            <button
              key={c.id}
              onMouseEnter={() => setIndex(i)}
              onClick={() => { c.run(); setOpen(false); setQuery('') }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: 'var(--sp-2) var(--sp-4)',
                background: i === index ? 'var(--bg-active)' : 'transparent',
                border: 'none', color: 'var(--text-primary)',
                fontSize: 'var(--fs-sm)', textAlign: 'left', cursor: 'pointer',
              }}
            >
              <span>{c.label}</span>
              <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                {c.hint ?? c.group}
              </span>
            </button>
          ))}
        </div>
        <footer style={{
          padding: 'var(--sp-2) var(--sp-4)', borderTop: '1px solid var(--border)',
          background: 'var(--bg-raised)', fontSize: 'var(--fs-2xs)',
          color: 'var(--text-muted)', display: 'flex', gap: 'var(--sp-4)',
        }}>
          <span>↑↓ navigate</span><span>↵ run</span><span>esc close</span>
        </footer>
      </div>
    </div>
  )
}
